import { Stack, Text } from '@mantine/core'
import React, { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import { STAKE_ADDRESS } from '@/constants/blockchain'
import Stake from '@/contracts/Stake'
import { ContractParams, useChainFunctions } from '@/hooks/useChain'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'

interface WithdrawConfirmationProps {
  close: () => void
  cancel: () => void
  selectedDays: number[]
}

const unlockMultipleConfig = (days: number[]): ContractParams => ({
  address: STAKE_ADDRESS,
  abi: Stake,
  functionName: 'unlockMultiple',
  args: [days],
})

const maxDays = 30

const WithdrawConfirmation: React.FC<WithdrawConfirmationProps> = ({
  close,
  cancel,
  selectedDays,
}) => {
  const { t } = useTranslation()

  const { writeContract } = useChainFunctions(l1NativeWagmiConfig)

  // ready confirmation
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [isReady, setIsReady] = useState<boolean>(false)

  // Calls
  const withdrawConfigCalls: ContractParams[] = useMemo(() => {
    // Split range into batches of maxDays
    const batches = Math.ceil(selectedDays.length / maxDays)
    const calls: ContractParams[] = []
    for (let i = 0; i < batches; i++) {
      const batchStart = i * maxDays
      const batchEnd = Math.min((i + 1) * maxDays, selectedDays.length)
      calls.push(unlockMultipleConfig(selectedDays.slice(batchStart, batchEnd)))
    }
    return calls
  }, [selectedDays])

  const handleWithdraw = useCallback(async () => {
    try {
      setIsLoading(true)
      for (let i = 0; i < withdrawConfigCalls.length; i++) {
        /* eslint-disable-next-line no-await-in-loop */
        await writeContract(withdrawConfigCalls[i])
      }
      setIsLoading(false)
      setIsReady(true)
    } catch (err: any) {
      console.error(err)
      setIsLoading(false)
      setError(err.message)
    }
  }, [withdrawConfigCalls, writeContract])

  return (
    <>
      {error && (
        <ErrorMessage
          message={t(
            'components.staking.staking.modal.withdraw.errorMessage',
            'Error while withdrawing'
          )}
          details={error}
        />
      )}
      {isLoading && (
        <ConfirmationLoading
          title={t(
            'components.staking.staking.modal.withdraw.loadingMessage',
            'Validating Withdraw'
          )}
        />
      )}
      {isReady && !isLoading && !error && (
        <ConfirmationReady
          message={t(
            'components.staking.staking.modal.withdraw.readyMessage',
            'Withdraw Submitted'
          )}
          tip={t(
            'components.staking.staking.modal.withdraw.tipMessage',
            'Check Wallet History for Transaction Status'
          )}
          success={close}
        />
      )}
      {!isReady && !error && !isLoading && (
        <>
          <Text>
            <Trans i18nKey="components.staking.staking.modal.withdraw.confirmation">
              Withdraw L1 tokens that have completed their staking term and can be used or re-staked
              on the network.
            </Trans>
          </Text>
          <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
            <RootButton onClick={cancel} secondary style1={false} expand>
              {t('buttons.cancel', 'Cancel')}
            </RootButton>
            <RootButton onClick={handleWithdraw} style1={false} expand>
              {t('buttons.confirm', 'Confirm')}
            </RootButton>
          </Stack>
        </>
      )}
    </>
  )
}

export default WithdrawConfirmation
